import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Lock, EnvelopeSimple, User } from "@phosphor-icons/react";
import Header from "@/components/app/Header";
import { useGoogleLogin } from "@react-oauth/google";


function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
      <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.3-1.6 3.9-5.4 3.9-3.2 0-5.9-2.7-5.9-6s2.7-6 5.9-6c1.8 0 3 .8 3.7 1.5l2.5-2.4C16.6 3.6 14.5 2.7 12 2.7A9.3 9.3 0 0 0 2.7 12c0 5.1 4.2 9.3 9.3 9.3 5.4 0 9-3.8 9-9.2 0-.6-.1-1.1-.2-1.6H12Z" />
      <path fill="#34A853" d="M2.7 12c0 1.7.5 3.3 1.5 4.7l3.6-2.8c-.2-.5-.4-1.2-.4-1.9s.1-1.3.4-1.9L4.2 7.3A9.1 9.1 0 0 0 2.7 12Z" />
      <path fill="#FBBC05" d="M12 21.3c2.5 0 4.6-.8 6.2-2.3l-3-2.4c-.8.6-1.9 1-3.2 1-2.5 0-4.6-1.7-5.4-4l-3.7 2.9A9.3 9.3 0 0 0 12 21.3Z" />
      <path fill="#4285F4" d="M18.2 19c1.8-1.7 2.8-4.2 2.8-7 0-.6-.1-1.1-.2-1.6H12v3.9h5.4c-.3 1.3-1.1 2.4-2.2 3.2l3 2.5Z" />
    </svg>
  );
}

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const { login, register, loginWithGoogle, user, refreshProfile } = useAuth();
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const busy = loading || googleLoading;

  useEffect(() => {
    let ignore = false;

    const redirectExistingSession = async () => {
      if (!user) {
        return;
      }

      const result = await refreshProfile();
      if (!ignore) {
        nav(result?.profile ? "/home" : "/onboarding", { replace: true });
      }
    };

    redirectExistingSession();

    return () => {
      ignore = true;
    };
  }, [nav, refreshProfile, user]);

  const loginWithGoogleAction = useGoogleLogin({
    onSuccess: async (response) => {
      setGoogleLoading(true);
      try {
        await loginWithGoogle(response.access_token, mode);
        const result = await refreshProfile();
        toast.success(mode === "register" ? "Account created with Google" : "Signed in with Google");
        nav(result?.profile ? "/home" : "/onboarding");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => toast.error("Google sign-in failed"),
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        await register(email, password, fullName);
        toast.success("Welcome to Opplio");
        nav("/onboarding");
      } else {
        await login(email, password);
        const result = await refreshProfile();
        toast.success("Signed in");
        nav(result?.profile ? "/home" : "/onboarding");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-6 hidden lg:block">
          <span className="tick-label">Access · secure</span>
          <h1 className="mt-3 font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            {mode === "register" ? <>Create your<br />scope.</> : <>Sign in to<br />your scope.</>}
          </h1>
          <p className="mt-6 text-ink-soft max-w-md whitespace-pre-line">
            {mode === "register"
              ? "Start with Google or email, then build your profile once. Your matches, drafts, and saved jobs stay attached to the same account."
              : "Pick up where you left off. Your profile, generated drafts, and match history stay in one place."}
          </p>
          <div className="mt-10 border border-slate-200 p-6 bg-slate-50/40 max-w-md">
            <span className="tick-label">Access paths</span>
            <div className="mt-4 space-y-3 text-sm text-ink-soft">
              <p className="flex items-start gap-3">
                <span className="mt-0.5 h-2 w-2 bg-brand flex-shrink-0" />
                Google sign-in creates or resumes the same Opplio account by verified email.
              </p>
              <p className="flex items-start gap-3">
                <span className="mt-0.5 h-2 w-2 bg-brand flex-shrink-0" />
                Email/password stays available for manual accounts and linked accounts.
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
          <div className="border border-slate-200 bg-white">
            <div className="flex">
              {["login", "register"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setMode(tab)}
                  className={`flex-1 py-4 text-xs uppercase tracking-wider font-semibold transition-colors ${
                    mode === tab ? "bg-ink text-white" : "bg-white text-ink-soft hover:text-ink"
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === "login" ? "Sign in" : "Create account"}
                </button>
              ))}
            </div>

            <div className="p-6 lg:p-8">
              <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm bg-slate-50/40 p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-ink-soft">
                  <GoogleMark />
                  Continue with Google
                </div>
                <p className="mt-2 text-sm text-ink-soft">
                  {mode === "register"
                    ? "Use your verified Google account to create Opplio access in one step."
                    : "Resume your account with the same Google identity you used before."}
                </p>
                <div className="mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => loginWithGoogleAction()}
                    disabled={busy}
                    className="w-full rounded-xl border-border text-ink bg-white hover:bg-slate-50 h-11 flex justify-center items-center gap-2 shadow-sm font-semibold text-sm"
                  >
                    <GoogleMark />
                    {mode === "register" ? "Sign up with Google" : "Sign in with Google"}
                  </Button>
                </div>
              </div>

              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-[11px] uppercase tracking-[0.18em] text-ink-mute font-semibold">
                  Or continue with email
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>

              <form onSubmit={submit} className="space-y-5">
                {mode === "register" && (
                  <div>
                    <Label htmlFor="full_name" className="tick-label">Full name</Label>
                    <div className="relative mt-2">
                      <User size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
                      <Input
                        id="full_name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                        className="rounded-xl pl-9 h-11 border-border focus-visible:ring-brand focus-visible:ring-1 focus-visible:ring-offset-0"
                        data-testid="full-name-input"
                        placeholder="Ada Lovelace"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="tick-label">Email</Label>
                  <div className="relative mt-2">
                    <EnvelopeSimple size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="rounded-xl pl-9 h-11 border-border focus-visible:ring-brand focus-visible:ring-1 focus-visible:ring-offset-0"
                      data-testid="email-input"
                      placeholder="you@work.com"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password" className="tick-label">Password</Label>
                  <div className="relative mt-2">
                    <Lock size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="rounded-xl pl-9 h-11 border-border focus-visible:ring-brand focus-visible:ring-1 focus-visible:ring-offset-0"
                      data-testid="password-input"
                      placeholder={mode === "register" ? "Create a password" : "Enter your password"}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-11"
                  data-testid="submit-auth-btn"
                >
                  {busy ? "Working..." : (mode === "login" ? "Sign in" : "Create account")} <ArrowRight size={14} weight="bold" className="ml-2" />
                </Button>
                <p className="text-xs text-ink-mute text-center">
                  By continuing you agree to keep the scope respectful.
                </p>
              </form>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-ink-soft">
            <Link to="/" className="uppercase tracking-wider font-semibold hover:text-brand">{"<- Back to home"}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
